import type { ActivityContextProps } from "./activity-context.type";

import { Descriptions, Flex } from "antd";
import { useTranslation } from "react-i18next";

import TextSkeleton from "@/components/Elements/TextSkeleton";
import { ActivityEvent } from "@/modules/activity/activity-event";
import { ActivityRestoreButton } from "@/modules/activity/activity-restore-button";
import { ActivitySubjectStatus } from "@/modules/activity/activity-subject-status";
import { CauserSubject } from "@/modules/causer-subject";

import { useActivityContextInfos } from "./activity-context.hook";

const { Item } = Descriptions;

export const ActivityContext = ({ id }: ActivityContextProps) => {
  const { t } = useTranslation();

  const {
    activity,
    loading,
    date,
    subject,
    subjectType,
    subjectModel,
    causer,
    causerModel,
    requestedBy,
    groupedIds,
  } = useActivityContextInfos(id);

  return (
    <Descriptions
      bordered
      column={1}
      styles={{ label: { width: "30%" }, content: { width: "70%" } }}
    >
      <Item label={t("activity:log.subject")}>
        <TextSkeleton loading={loading}>
          <CauserSubject type="subject" model={subjectModel} item={subject} />
        </TextSkeleton>
      </Item>
      {groupedIds && (
        <Item label={t("activity:log.subjectIds")}>
          <TextSkeleton loading={loading}>{groupedIds}</TextSkeleton>
        </Item>
      )}
      <Item label={t("activity:log.subjectType")}>
        <TextSkeleton loading={loading}>{subjectType}</TextSkeleton>
      </Item>
      <Item label={t("activity:log.event")}>
        <TextSkeleton loading={loading}>
          {activity && <ActivityEvent activity={activity} />}
        </TextSkeleton>
      </Item>
      <Item label={t("activity:log.date")}>
        <TextSkeleton loading={loading}>{date}</TextSkeleton>
      </Item>
      <Item label={t("activity:log.causedBy")}>
        <TextSkeleton loading={loading}>
          <CauserSubject type="causer" model={causerModel} item={causer} />
        </TextSkeleton>
      </Item>
      <Item label={t("activity:log.requestedBy")}>
        <TextSkeleton loading={loading}>{requestedBy}</TextSkeleton>
      </Item>
      <Item label={t("activity:log.currentState")}>
        <TextSkeleton loading={loading}>
          {activity && (
            <Flex gap="small">
              <ActivitySubjectStatus activity={activity} />
              <ActivityRestoreButton activity={activity} />
            </Flex>
          )}
        </TextSkeleton>
      </Item>
    </Descriptions>
  );
};
